import { useEffect, useState } from "react";
import { ScrollView, View, Text, Pressable, ActivityIndicator } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { doc, onSnapshot, collection, deleteDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { setTrainingAttendance, deleteTrainingSeries, cancelTrainingSession } from "@/lib/services";
import { colors, radius } from "@/lib/theme";
import { PencilIcon, CloseCircleIcon, TrashIcon } from "@/components/Icons";
import ContentWidth from "@/components/ContentWidth";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmSheet from "@/components/ConfirmSheet";
import type { Training, TrainingAttendance } from "@/lib/types";

function formatDate(d: string): string {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return d; }
}

function formatTime(t: string): string {
  try {
    const [h, m] = t.split(":");
    const hour = parseInt(h);
    if (hour >= 12) return `${hour === 12 ? 12 : hour - 12}:${m}pm`;
    return `${hour === 0 ? 12 : hour}:${m}am`;
  } catch { return t; }
}

export default function TrainingDetailScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ id: string }>();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { confirmProps, confirm } = useConfirm();

  const [training, setTraining] = useState<(Training & { id: string }) | null>(null);
  const [attendance, setAttendance] = useState<TrainingAttendance[]>([]);
  const [myStatus, setMyStatus] = useState<"in" | "out" | null>(null);
  const [myFeePaid, setMyFeePaid] = useState(false);
  const [saving, setSaving] = useState(false);

  const isAdmin = membership?.role === "admin";

  useEffect(() => {
    if (!club || !params.id) return;
    const unsub = onSnapshot(doc(db, "clubs", club.id, "trainings", params.id), (snap) => {
      if (snap.exists()) setTraining({ id: snap.id, ...(snap.data() as Training) });
    });
    return unsub;
  }, [club?.id, params.id]);

  useEffect(() => {
    if (!club || !params.id) return;
    const unsub = onSnapshot(collection(db, "clubs", club.id, "trainings", params.id, "attendance"), (snap) => {
      setAttendance(snap.docs.map((d) => d.data() as TrainingAttendance));
    });
    return unsub;
  }, [club?.id, params.id]);

  useEffect(() => {
    if (!club || !params.id || !user) return;
    const unsub = onSnapshot(doc(db, "clubs", club.id, "trainings", params.id, "attendance", user.uid), (snap) => {
      setMyStatus(snap.exists() ? (snap.data().status as "in" | "out") : null);
    });
    return unsub;
  }, [club?.id, params.id, user?.uid]);

  useEffect(() => {
    if (!club || !params.id || !user) return;
    const unsub = onSnapshot(doc(db, "clubs", club.id, "trainings", params.id, "fees", user.uid), (snap) => {
      setMyFeePaid(snap.exists() ? !!snap.data().paid : false);
    });
    return unsub;
  }, [club?.id, params.id, user?.uid]);

  if (!training) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator color={colors.brand} />
      </View>
    );
  }

  const attending = attendance.filter((a) => a.status === "in");
  const notComing = attendance.filter((a) => a.status === "out");

  const handleSet = async (next: "in" | "out") => {
    if (!club || !user || !membership) return;
    setSaving(true);
    try {
      await setTrainingAttendance(club.id, training.id, {
        userId: user.uid,
        displayName: membership.displayName,
        status: next,
        trainingDate: training.date,
        trainingTitle: training.title,
        fee: training.fee,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSession = async () => {
    if (!club) return;
    const ok = await confirm({
      title: "Cancel this session",
      message: "This session will be cancelled. Attendees will see it removed.",
      confirmLabel: "Cancel session",
      destructive: true,
    });
    if (!ok) return;
    await cancelTrainingSession(club.id, training.id);
    router.back();
  };

  const handleDeleteSeries = async () => {
    if (!club) return;
    const ok = await confirm({
      title: "Delete future sessions",
      message: "All future sessions in this series will be deleted. Past sessions are preserved.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await deleteTrainingSeries(club.id, training.seriesId, "future");
    router.back();
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.bg }}>
      <ContentWidth>
        <View style={{ padding: 20 }}>
          <View style={{ backgroundColor: colors.amber50, borderColor: colors.amber200, borderWidth: 1, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999, alignSelf: "flex-start", marginBottom: 8 }}>
            <Text style={{ fontSize: 10, fontWeight: "800", color: colors.amber, textTransform: "uppercase", letterSpacing: 0.5 }}>Training</Text>
          </View>
          <Text style={{ fontSize: 24, fontWeight: "800", color: colors.ink }}>{training.title}</Text>
          <Text style={{ fontSize: 14, color: colors.inkMuted, marginTop: 4 }}>
            {formatDate(training.date)} · {formatTime(training.time)} · {training.duration} min
          </Text>
          <Text style={{ fontSize: 14, color: colors.inkMuted, marginTop: 2 }}>{training.location}</Text>
          {training.fee > 0 && (
            <Text style={{ fontSize: 14, color: colors.brand, marginTop: 4, fontWeight: "700" }}>
              £{(training.fee / 100).toFixed(2)} per session
            </Text>
          )}
          {isAdmin && (
            <View style={{ flexDirection: "row", gap: 10, marginTop: 12 }}>
              <Pressable
                onPress={() => router.push(`/(app)/edit-training?seriesId=${training.seriesId}&title=${encodeURIComponent(training.title)}&time=${training.time}&duration=${training.duration}&location=${encodeURIComponent(training.location)}&fee=${training.fee}`)}
                style={({ pressed }) => ({
                  width: 40, height: 40, borderRadius: 20,
                  backgroundColor: colors.brand50, borderWidth: 1, borderColor: "#86efac",
                  alignItems: "center", justifyContent: "center",
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <PencilIcon size={18} color={colors.brandDark} />
              </Pressable>
              <Pressable
                onPress={handleCancelSession}
                style={({ pressed }) => ({
                  width: 40, height: 40, borderRadius: 20,
                  backgroundColor: colors.amber50, borderWidth: 1, borderColor: colors.amber200,
                  alignItems: "center", justifyContent: "center",
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <CloseCircleIcon size={18} color={colors.amber} />
              </Pressable>
              <Pressable
                onPress={handleDeleteSeries}
                style={({ pressed }) => ({
                  width: 40, height: 40, borderRadius: 20,
                  backgroundColor: colors.dangerLight, borderWidth: 1, borderColor: "#fecaca",
                  alignItems: "center", justifyContent: "center",
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <TrashIcon size={18} color={colors.danger} />
              </Pressable>
            </View>
          )}
        </View>

        <View style={{ padding: 20, paddingTop: 0 }}>
          <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            Your attendance
          </Text>
          <View style={{ flexDirection: "row", gap: 8 }}>
            {(["in", "out"] as const).map((s) => {
              const active = myStatus === s;
              const c = s === "in"
                ? { bg: colors.brand50, border: colors.brandLight, text: colors.brandDark, label: "I'm in" }
                : { bg: colors.dangerLight, border: "#fecaca", text: colors.danger, label: "Not coming" };
              return (
                <Pressable
                  key={s}
                  onPress={() => handleSet(s)}
                  disabled={saving}
                  style={({ pressed }) => ({
                    flex: 1,
                    paddingVertical: 14,
                    borderRadius: radius.md,
                    alignItems: "center",
                    backgroundColor: active ? c.bg : colors.white,
                    borderWidth: 1.5,
                    borderColor: active ? c.border : colors.line,
                    opacity: pressed ? 0.7 : saving ? 0.5 : 1,
                  })}
                >
                  <Text style={{ fontSize: 14, fontWeight: "700", color: active ? c.text : colors.inkMuted }}>
                    {c.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          {myStatus === "in" && training.fee > 0 && !myFeePaid && (
            <Pressable
              onPress={() => router.push(`/(app)/pay?amount=${training.fee}&description=${encodeURIComponent(`Training - ${training.title}`)}&feeId=${user!.uid}&feeType=training&trainingId=${training.id}`)}
              style={({ pressed }) => ({ marginTop: 16, opacity: pressed ? 0.85 : 1 })}
            >
              <View style={{ backgroundColor: colors.brand, padding: 14, borderRadius: radius.md, alignItems: "center" }}>
                <Text style={{ color: "#fff", fontWeight: "700", fontSize: 14 }}>
                  Pay £{(training.fee / 100).toFixed(2)}
                </Text>
              </View>
            </Pressable>
          )}
          {myStatus === "in" && training.fee > 0 && myFeePaid && (
            <View style={{ marginTop: 16, backgroundColor: colors.brand50, borderColor: "#86efac", borderWidth: 1, padding: 14, borderRadius: radius.md, alignItems: "center" }}>
              <Text style={{ color: colors.brandDark, fontWeight: "700" }}>✓ Paid</Text>
            </View>
          )}
        </View>

        <View style={{ padding: 20, paddingTop: 0 }}>
          <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            Attending ({attending.length})
          </Text>
          {attending.length === 0 ? (
            <Text style={{ fontSize: 13, color: colors.inkMuted, padding: 16, backgroundColor: colors.white, borderRadius: radius.md, borderWidth: 1, borderColor: colors.line, textAlign: "center" }}>
              No one yet
            </Text>
          ) : (
            <View style={{ backgroundColor: colors.white, borderRadius: radius.md, borderWidth: 1, borderColor: colors.line }}>
              {attending.map((a, i) => (
                <View key={a.userId} style={{ padding: 14, borderBottomWidth: i < attending.length - 1 ? 1 : 0, borderBottomColor: colors.line, flexDirection: "row", alignItems: "center" }}>
                  <Text style={{ fontSize: 14, fontWeight: "600", color: colors.ink, flex: 1 }}>{a.displayName}</Text>
                  <Text style={{ fontSize: 16 }}>✅</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {notComing.length > 0 && (
          <View style={{ padding: 20, paddingTop: 0 }}>
            <Text style={{ fontSize: 12, fontWeight: "800", color: colors.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
              Not coming ({notComing.length})
            </Text>
            <View style={{ backgroundColor: colors.white, borderRadius: radius.md, borderWidth: 1, borderColor: colors.line }}>
              {notComing.map((a, i) => (
                <View key={a.userId} style={{ padding: 14, borderBottomWidth: i < notComing.length - 1 ? 1 : 0, borderBottomColor: colors.line, flexDirection: "row", alignItems: "center" }}>
                  <Text style={{ fontSize: 14, fontWeight: "600", color: colors.inkMuted, flex: 1 }}>{a.displayName}</Text>
                  <Text style={{ fontSize: 16 }}>❌</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        <View style={{ height: 40 }} />
      </ContentWidth>
      <ConfirmSheet {...confirmProps} />
    </ScrollView>
  );
}
